/**
 * Exportación a Excel (.xlsx) de la Tabla Dinámica (pivote).
 *
 * A diferencia de `exportarMatrizExcel` (matriz Año › Mes › Semana), este helper
 * vuelca la estructura del pivote configurable:
 *  - Encabezado de 2 filas: grupo de columna (valores de las dimensiones de
 *    columna, o "Total") sobre la etiqueta de cada métrica.
 *  - Filas jerárquicas indentadas por nivel, con subtotales por nivel sombreados
 *    y una fila final de Gran Total.
 *  - VALORES NUMÉRICOS CRUDOS + `numFmt` (no el texto "₡1 234"), para que Excel
 *    pueda sumar/filtrar/graficar.
 *  - Replica el sombreado de subtotales y el heatmap azul tenue de las celdas
 *    hoja, tal como se ve en pantalla.
 *
 * exceljs se importa de forma DINÁMICA para no cargar la librería (~1 MB) en el
 * bundle inicial; solo se descarga al exportar.
 */

export type FormatoMetrica = 'moneda' | 'numero';
export type AgregacionExport = 'Suma' | 'Promedio' | 'Conteo' | 'Máximo';

/** Una columna del export = (grupo de columna) × (métrica). */
export interface ColumnaPivotExport {
  /** Etiqueta del grupo de columna ("Total" o "Directo / 2024"). */
  etiquetaGrupo: string;
  etiquetaMetrica: string;
  formato: FormatoMetrica;
}

/** Una celda de datos: valor crudo + intensidad de heatmap (0..1) o null. */
export interface CeldaPivotExport {
  /** NaN = celda vacía (no se escribe). */
  valor: number;
  /** Intensidad 0..1 para el heatmap azul; null = sin heatmap. */
  heat: number | null;
}

/** Una fila del árbol aplanado. */
export interface FilaPivotExport {
  /** 0 = primer nivel de filas; aumenta con la profundidad. */
  depth: number;
  etiqueta: string;
  /** true si el nodo tiene hijos (fila de subtotal). */
  esSubtotal: boolean;
  /** true solo para la fila final de Gran Total. */
  esGranTotal?: boolean;
  /** Alineadas 1:1 con `columnas`. */
  celdas: CeldaPivotExport[];
}

export interface OpcionesPivotExport {
  nombreArchivo: string;
  tituloHoja: string;
  /** Encabezado de la columna de jerarquía (p.ej. "Proyecto › Obra"). */
  encabezadoJerarquia: string;
  columnas: ColumnaPivotExport[];
  filas: FilaPivotExport[];
  agregacion: AgregacionExport;
}

/** Formato de número de Excel según métrica + agregación (coincide con la vista). */
function numFmtDe(formato: FormatoMetrica, agg: AgregacionExport): string {
  if (agg === 'Conteo') return '#,##0';
  return formato === 'moneda' ? '"₡"#,##0' : '#,##0.0';
}

/** ARGB del heatmap azul (rgb 59,130,246) mezclado con blanco, igual que la web. */
function argbHeatmap(intensidad: number | null): string | null {
  if (intensidad == null || Number.isNaN(intensidad)) return null;
  const t = Math.min(1, Math.max(0, intensidad));
  const alpha = 0.08 + t * 0.45;
  const blend = (ch: number) => Math.round(255 * (1 - alpha) + ch * alpha);
  const h = (n: number) => n.toString(16).padStart(2, '0').toUpperCase();
  return `FF${h(blend(59))}${h(blend(130))}${h(blend(246))}`;
}

/** Sombreado de subtotal por profundidad (emerald-100 / emerald-50 / lime-50). */
function argbSubtotal(depth: number): string {
  if (depth === 0) return 'FFD1FAE5';
  if (depth === 1) return 'FFECFDF5';
  return 'FFF7FEE7';
}

const ARGB_GRAN_TOTAL = 'FFE5E7EB';
const ARGB_HEADER_1 = 'FF064E3B'; // emerald-900
const ARGB_HEADER_2 = 'FF065F46'; // emerald-800

/** Genera y dispara la descarga del .xlsx en el navegador. */
export async function exportarPivotExcel(opts: OpcionesPivotExport): Promise<void> {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Adelante BI Obras';
  wb.created = new Date();
  const ws = wb.addWorksheet(opts.tituloHoja, {
    views: [{ state: 'frozen', xSplit: 1, ySplit: 2 }],
  });

  const totalCols = opts.columnas.length + 1; // +1 por la columna de jerarquía

  // --- Fila 1: grupo de columna (merge sobre sus métricas) ---
  // --- Fila 2: etiqueta de métrica ---
  ws.getCell(2, 1).value = opts.encabezadoJerarquia;
  let idx = 2; // las columnas de datos empiezan en la col 2 (1 = jerarquía)
  const grupos: { etiqueta: string; ini: number; fin: number }[] = [];
  for (const c of opts.columnas) {
    const ult = grupos[grupos.length - 1];
    if (ult && ult.etiqueta === c.etiquetaGrupo) ult.fin = idx;
    else grupos.push({ etiqueta: c.etiquetaGrupo, ini: idx, fin: idx });
    ws.getCell(2, idx).value = c.etiquetaMetrica;
    idx++;
  }
  for (const { etiqueta, ini, fin } of grupos) {
    if (fin > ini) ws.mergeCells(1, ini, 1, fin);
    ws.getCell(1, ini).value = etiqueta;
  }

  // Estilo de encabezado (filas 1 y 2).
  for (let c = 1; c <= totalCols; c++) {
    const c1 = ws.getCell(1, c);
    c1.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    c1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB_HEADER_1 } };
    c1.alignment = { horizontal: c === 1 ? 'left' : 'center', vertical: 'middle' };
    const c2 = ws.getCell(2, c);
    c2.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    c2.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB_HEADER_2 } };
    c2.alignment = { horizontal: c === 1 ? 'left' : 'right', vertical: 'middle' };
  }

  // --- Filas de datos ---
  for (const fila of opts.filas) {
    const fileRow = ws.addRow([]);
    const sangria = '    '.repeat(fila.depth);
    const celdaJerarquia = fileRow.getCell(1);
    celdaJerarquia.value = `${sangria}${fila.etiqueta}`;
    if (fila.esSubtotal || fila.esGranTotal) celdaJerarquia.font = { bold: true };

    // Color de fondo de fila: Gran Total > subtotal > (celda: heatmap).
    const fondoFila = fila.esGranTotal
      ? ARGB_GRAN_TOTAL
      : fila.esSubtotal
        ? argbSubtotal(fila.depth)
        : null;
    if (fondoFila) {
      celdaJerarquia.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fondoFila } };
    }

    opts.columnas.forEach((col, i) => {
      const cell = fileRow.getCell(i + 2);
      const dato = fila.celdas[i];
      if (dato && !Number.isNaN(dato.valor)) {
        cell.value = dato.valor;
        cell.numFmt = numFmtDe(col.formato, opts.agregacion);
      }
      cell.alignment = { horizontal: 'right' };
      const argb = fondoFila ?? argbHeatmap(dato?.heat ?? null);
      if (argb) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
      }
    });
  }

  // Ancho de columnas.
  ws.getColumn(1).width = 44;
  for (let c = 2; c <= totalCols; c++) ws.getColumn(c).width = 15;

  // Descargar.
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = opts.nombreArchivo.endsWith('.xlsx')
    ? opts.nombreArchivo
    : `${opts.nombreArchivo}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
